import Links from '@/shared/constants/navegacao';
import { ApiReturn } from '@/modules/api.service';
import { Column, DataTable } from '@/shared/components/tables/DataTable';
import { Fragment, useEffect, useState } from 'react';
import { LerTipoMovimentacoes } from '@/modules/business/movimentacoes_tipos/movimentacaotipo.service';
import { Link } from 'react-router-dom';
import { ReadMovimentacaoTipoDto } from '@/api.types';
import { MovimentacaoTipoPDF } from '@/modules/business/movimentacoes_tipos/pages/ReportPage';
import { exportFromJsxToPdf } from '@/shared/utils/pdf';
import { toast } from 'react-toastify';
import { useDataTableState } from '@/shared/components/tables/useDataTableState';
import { useBreadcrumbActions } from '@/shared/contexts/BreadcrumbActionsContext';
import { CACHE_MOVIMENTACAO_TIPO_FILTROS } from '@/shared/constants/strings';

type FiltrosTipoMovimentacao = {
  ehNucleo?: boolean;
  filter?: string;
};

function lerFiltrosSalvos(): FiltrosTipoMovimentacao {
  try {
    return JSON.parse(localStorage.getItem(CACHE_MOVIMENTACAO_TIPO_FILTROS) || '{}');
  } catch {
    return {};
  }
}

export default function MovimentacaoTipoIndex() {
  const { setActions } = useBreadcrumbActions();
  const filtrosSalvos = lerFiltrosSalvos();

  const [ehNucleo, setEhNucleo] = useState(() => Boolean(filtrosSalvos.ehNucleo));
  const [registros, setRegistros] = useState<ApiReturn<ReadMovimentacaoTipoDto>>({ objects: [], count: 0, });
  const tableState = useDataTableState('descricao');

  useEffect(() => {
    if (!tableState.filter && filtrosSalvos.filter) {
      tableState.setFilter(filtrosSalvos.filter);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    localStorage.setItem(
      CACHE_MOVIMENTACAO_TIPO_FILTROS,
      JSON.stringify({ ehNucleo, filter: tableState.filter }),
    );
  }, [ehNucleo, tableState.filter]);

  const handleBuscar = () => {
    LerTipoMovimentacoes(
      (tableState.currentPage - 1) * tableState.pageSize,
      tableState.pageSize,
      ehNucleo,
      tableState.filter,
    )
      .then((result) => {
        setRegistros(result);
      })
      .catch((res) => {
        toast.error(String(res) || '');
      });
  }

  useEffect(() => {
    handleBuscar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ehNucleo]);

  const handleGerarPDF = async () => {
    try {
      const registrosDefensoria = await LerTipoMovimentacoes(0, 10000, false);
      const registrosNucleo = await LerTipoMovimentacoes(0, 10000, true);

      const todosRegistros = {
        objects: [
          ...registrosDefensoria.objects.map((item: any) => ({ ...item, ehNucleo: false })),
          ...registrosNucleo.objects.map((item: any) => ({ ...item, ehNucleo: true }))
        ],
        count: registrosDefensoria.count + registrosNucleo.count
      };

      exportFromJsxToPdf(
        <MovimentacaoTipoPDF registros={todosRegistros} />
      );
    } catch (error) {
      toast.error('Erro ao gerar PDF: ' + (error as Error).message);
    }
  };

  useEffect(() => {
    setActions({
      primary: { label: 'Cadastrar', href: Links.Movimentacoes_Tipos_Criar, onClick: () => { } },
      print: { label: 'Imprimir', onClick: () => handleGerarPDF() },
    });
    return () => setActions({});
  }, [setActions]);

  const marca = (marcado: boolean) => (
    <input type="checkbox" checked={Boolean(marcado)} readOnly />
  );

  const colunas: Column<ReadMovimentacaoTipoDto>[] = [
    { key: 'descricao', header: 'Descrição' },
    { key: 'inicial', header: 'Petição Inicial', render: (row) => marca(row.inicial) },
    { key: 'nucleo', header: 'Núcleo', render: (row) => marca(row.nucleo) },
    { key: 'relatorioCivel', header: 'Relatório Cível', render: (row) => marca(row.relatorioCivel) },
    { key: 'relatorioCriminal', header: 'Relatório Criminal', render: (row) => marca(row.relatorioCriminal) },
    {
      key: 'acoes', header: 'Ações', render: (row) =>
        <Link className="link-secondary" to={Links.Movimentacoes_Tipos_Editar + '/' + row.id}>
          Editar
        </Link>
    },
  ];

  return (
    <Fragment>
      <div className="row">
        <div className="col-12">
          <h3 className="mb-0">Tipos de Movimentações</h3>
          <span className="blockquote-footer text-end">
            Tipos de Movimentações
          </span>
        </div>
        <hr className="pb-4" />

        {registros &&
          <DataTable
            columns={colunas}
            data={registros.objects}
            initialSortKey='descricao'
            onChange={handleBuscar}
            serverSide={true}
            states={tableState}
            totalRecords={registros.count}
            renderFilters={() => (
              <div className="col-auto form-check mt-4" title="Exibir Núcleos.">
                <input
                  id="nucleo"
                  className="form-check-input"
                  type="checkbox"
                  checked={ehNucleo}
                  onChange={(e) => {
                    setEhNucleo(e.target.checked);
                    tableState.setCurrentPage(1);
                  }} />
                <label htmlFor="nucleo" className="form-check-label">
                  Núcleos
                </label>
              </div>
            )}
          />
        }
      </div>
    </Fragment>
  );
}
